import lottie from 'lottie-web'
import { overlayDebugError, overlayDebugLog, overlayDebugWarn } from '../helpers/overlayDebug'

/** Une slide est traitée par SpaLottie si elle contient l'overlay lottie. */
const hasSpaLottieOverlay = (slide) =>
  Boolean(slide && slide.querySelector('.js-spa-lottie-overlay'))
const isSpaTemplateSlide = (slide) =>
  Boolean(slide && slide.classList.contains('template-spa'))

class SpaLottie {
  constructor() {
    this._onOverlayOpened = this.onOverlayOpened.bind(this)
    this._onOverlayClosed = this.onOverlayClosed.bind(this)
    this._onSlideChange = this.onSlideChange.bind(this)
    this.initialize()
  }

  initialize() {
    this.bindUI()
    this.bindEvents()
  }

  bindUI() {
    this.ui = {}
    this.animations = new Map()
    this.currentAnimationId = null
    this.isProcessing = false
    this.isInitialOverlay = true
  }

  bindEvents() {
    this._onFetchDone = this.onFetchDone.bind(this)
    window.addEventListener('overlay:opened', this._onOverlayOpened)
    window.addEventListener('overlay:closed', this._onOverlayClosed)
    window.addEventListener('fetch:done', this._onFetchDone)
    this.waitForSlider()
  }

  onFetchDone() {
    if (!window.sliderdefault || !window.sliderdefault.slides) {
      overlayDebugLog('SpaLottie.onFetchDone: pas de sliderdefault')
      return
    }
    const activeSlide = window.sliderdefault.slides[window.sliderdefault.activeIndex]
    if (!activeSlide) {
      overlayDebugLog('SpaLottie.onFetchDone: pas de slide active')
      return
    }
    if (isSpaTemplateSlide(activeSlide)) {
      this.ensureOverlayForSlide(activeSlide)
    }
    if (!hasSpaLottieOverlay(activeSlide)) {
      this.hideSpaLottieLoader()
      overlayDebugLog('SpaLottie.onFetchDone: slide active non SPA', {
        activeIndex: window.sliderdefault.activeIndex,
        className: activeSlide.className,
      })
      return
    }
    if (!this.isInitialOverlay) {
      overlayDebugLog('SpaLottie.onFetchDone: ignoré (pas overlay initial)')
      return
    }
    overlayDebugLog('SpaLottie.onFetchDone → initSpaLottie')
    this.initSpaLottie()
  }

  waitForSlider() {
    const checkSlider = () => {
      if (window.sliderdefault) {
        window.sliderdefault.on('slideChange', this._onSlideChange)
      } else {
        setTimeout(checkSlider, 100)
      }
    }
    checkSlider()
  }

  onOverlayOpened() {
    overlayDebugLog('SpaLottie.onOverlayOpened')
    document
      .querySelectorAll(
        '.swiper-slide .js-overlay-child'
      )
      .forEach((el) => {
        el.style.removeProperty('opacity')
        el.style.removeProperty('visibility')
        el.style.removeProperty('transition')
      })
    this.isInitialOverlay = true
    this.waitForSliderReady(() => {
      if (window.sliderdefault) {
        if (window.sliderdefault.off) {
          window.sliderdefault.off('slideChange', this._onSlideChange)
        }
        window.sliderdefault.on('slideChange', this._onSlideChange)
      } else {
        overlayDebugWarn('SpaLottie: Pas de sliderdefault lors de l\'ouverture overlay')
      }
      const activeSlide = window.sliderdefault && window.sliderdefault.slides
        ? window.sliderdefault.slides[window.sliderdefault.activeIndex]
        : null
      if (activeSlide && (isSpaTemplateSlide(activeSlide) || hasSpaLottieOverlay(activeSlide))) {
        this.showSpaLottieLoader()
        this.initSpaLottie()
      }
      this.hideSpaLottieLoader()
    })
  }

  onOverlayClosed() {
    this.hideSpaLottieLoader()
    this.cleanupAllAnimations()
    this.resetAllOverlays()
    this.isInitialOverlay = true
  }

  onSlideChange() {
    overlayDebugLog('SpaLottie.onSlideChange', {
      activeIndex: window.sliderdefault ? window.sliderdefault.activeIndex : null,
    })
    this.isInitialOverlay = false

    this.cleanupCurrentAnimation()

    this.hideAllLottieOverlays()

    const activeSlide = window.sliderdefault && window.sliderdefault.slides
      ? window.sliderdefault.slides[window.sliderdefault.activeIndex]
      : null
    if (activeSlide && hasSpaLottieOverlay(activeSlide)) {
      const overlay = activeSlide.querySelector('.js-spa-lottie-overlay')
      if (overlay) {
        overlay.classList.add('is-hidden')
        const container = overlay.querySelector('.js-spa-lottie-container')
        if (container) {
          container.innerHTML = ''
        }
      }
      const slideContent = activeSlide.querySelector('.js-overlay-child')
      if (slideContent) {
        slideContent.style.opacity = '1'
        slideContent.style.visibility = 'visible'
        slideContent.style.transition = 'none'
        setTimeout(() => {
          slideContent.style.transition = ''
        }, 100)
      }
    }
  }

  processSlideChange() {
    if (this.isProcessing) {
      return
    }

    this.isProcessing = true

    const delay = window.sliderdefault && window.sliderdefault.isBeginning ? 50 : 150

    setTimeout(() => {
      this.initSpaLottie()
      this.isProcessing = false
    }, delay)
  }

  hideAllLottieOverlays() {
    const allOverlays = document.querySelectorAll('.js-spa-lottie-overlay')
    allOverlays.forEach((overlay) => {
      overlay.classList.add('is-hidden')

      const slide = overlay.closest('.swiper-slide')
      if (slide && hasSpaLottieOverlay(slide)) {
        const slideContent = slide.querySelector('.js-overlay-child')
        if (slideContent) {
          slideContent.style.opacity = '1'
          slideContent.style.visibility = 'visible'
          slideContent.style.transition = 'none'

          setTimeout(() => {
            slideContent.style.transition = ''
          }, 100)
        }
      }
    })
  }

  cleanupAllAnimations() {
    this.animations.forEach((animationData) => {
      if (animationData.animation && animationData.animation.destroy) {
        animationData.animation.destroy()
      }
    })
    this.animations.clear()

    this.currentAnimationId = null
  }

  cleanupCurrentAnimation() {
    if (this.currentAnimationId && this.animations.has(this.currentAnimationId)) {
      const animationData = this.animations.get(this.currentAnimationId)
      if (animationData.animation && animationData.animation.destroy) {
        animationData.animation.destroy()
      }
      this.animations.delete(this.currentAnimationId)
      this.currentAnimationId = null
    }
  }

  resetAllOverlays() {
    const allOverlays = document.querySelectorAll('.js-spa-lottie-overlay')
    allOverlays.forEach((overlay) => {
      this.resetOverlay(overlay)
    })
  }

  resetOverlay(overlay) {
    overlay.classList.remove('is-hidden')

    const container = overlay.querySelector('.js-spa-lottie-container')
    if (container) {
      container.innerHTML = ''
    }

  }

  waitForSliderReady(callback) {
    const checkReady = () => {
      if (window.sliderdefault && window.sliderdefault.slides && window.sliderdefault.slides.length > 0) {
        const activeSlide = window.sliderdefault.slides[window.sliderdefault.activeIndex]
        if (activeSlide) {
          callback()
          return
        }
      }
      setTimeout(checkReady, 50)
    }
    checkReady()
  }

  initSpaLottie() {
    this.cleanupCurrentAnimation()

    const activeSlide = window.sliderdefault.slides[window.sliderdefault.activeIndex]
    if (!activeSlide) {
      overlayDebugLog('SpaLottie.initSpaLottie: pas de slide active')
      return
    }

    if (isSpaTemplateSlide(activeSlide)) {
      this.ensureOverlayForSlide(activeSlide)
    }
    overlayDebugLog('SpaLottie.initSpaLottie état slide', {
      className: activeSlide.className,
      hasSlideContent: Boolean(activeSlide.querySelector('.js-overlay-child')),
      hasOverlay: hasSpaLottieOverlay(activeSlide),
      resolvedPath: this.resolveSpaLottiePath(),
    })
    if (!hasSpaLottieOverlay(activeSlide)) {
      overlayDebugLog('SpaLottie.initSpaLottie: slide sans overlay Lottie', {
        className: activeSlide.className,
      })
      return
    }

    const overlay = activeSlide.querySelector('.js-spa-lottie-overlay')
    if (!overlay) {
      overlayDebugLog('SpaLottie.initSpaLottie: pas d’overlay Lottie sur la slide')
      return
    }

    if (!this.isInitialOverlay) {
      overlayDebugLog('SpaLottie.initSpaLottie: ignoré (pas overlay initial)')
      return
    }

    overlayDebugLog('SpaLottie.initSpaLottie → processOverlay')
    this.processOverlay(overlay)
  }

  processOverlay(overlay) {
    overlay.classList.remove('is-hidden')

    const container = overlay.querySelector('.js-spa-lottie-container')
    if (!container) {
      this.hideSpaLottie(overlay)
      return
    }

    const path = container.getAttribute('data-path')
    if (!path) {
      overlayDebugWarn('SpaLottie.processOverlay: data-path manquant')
      this.hideSpaLottie(overlay)
      return
    }

    overlayDebugLog('SpaLottie.processOverlay', { path })

    if (container.querySelector('svg')) {
      overlayDebugLog('SpaLottie.processOverlay: SVG déjà présent, skip')
      return
    }

    container.innerHTML = ''
    this.createNewAnimation(container, path, overlay)
  }

  resolveSpaLottiePath() {
    const htmlPath = document.documentElement.getAttribute('data-spa-lottie-path')
    if (htmlPath) {
      return htmlPath
    }
    const existingPathNode = document.querySelector('.js-spa-lottie-container[data-path]')
    if (existingPathNode) {
      return existingPathNode.getAttribute('data-path')
    }
    return ''
  }

  ensureOverlayForSlide(slide) {
    if (!slide || !isSpaTemplateSlide(slide) || hasSpaLottieOverlay(slide)) {
      return
    }
    const body = slide.querySelector('.overlay__body')
    const slideContent = slide.querySelector('.js-overlay-child')
    if (!body || !slideContent) {
      overlayDebugWarn('SpaLottie.ensureOverlayForSlide: .overlay__body / .js-overlay-child introuvable', {
        className: slide.className,
      })
      return
    }
    const path = this.resolveSpaLottiePath()
    if (!path) {
      overlayDebugWarn('SpaLottie.ensureOverlayForSlide: path introuvable')
      return
    }
    const overlay = document.createElement('div')
    overlay.className = 'spa-lottie-overlay js-spa-lottie-overlay'
    overlay.setAttribute('aria-hidden', 'false')
    overlay.innerHTML = `<div class="spa-lottie-container js-spa-lottie-container" data-path="${path}"></div>`
    body.insertBefore(overlay, slideContent)
    overlayDebugLog('SpaLottie.ensureOverlayForSlide: overlay injecté dynamiquement', {
      className: slide.className,
      path,
    })
  }

  createNewAnimation(container, path, overlay) {
    const animation = lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: false,
      speed: 1.15,
      autoplay: false,
      path: path
    })

    this.setupAnimation(animation, overlay)
  }

  setupAnimation(animation, overlay) {
    const animationId = Date.now() + Math.random()
    this.currentAnimationId = animationId

    this.animations.set(animationId, {
      animation: animation,
      overlay: overlay,
      isComplete: false,
      isError: false,
      startTime: Date.now()
    })

    let animationComplete = false
    let animationError = false

    animation.addEventListener('data_ready', () => {
      animation.play()
    })

    animation.addEventListener('complete', () => {
      if (!animationComplete) {
        animationComplete = true
        this.animations.get(animationId).isComplete = true
        this.finishAnimation(overlay, animationId)
      }
    })

    animation.addEventListener('error', () => {
      overlayDebugError('SpaLottie: Animation error')
      console.error('SpaLottie: Animation error')
      if (!animationError) {
        animationError = true
        this.animations.get(animationId).isError = true
        setTimeout(() => {
          this.finishAnimation(overlay, animationId)
        }, 1000)
      }
    })

    const checkAnimationState = () => {
      if (animationComplete || animationError) {
        return
      }

      if (animation && animation.isLoaded && animation.totalFrames) {
        const currentFrame = animation.currentFrame
        const totalFrames = animation.totalFrames

        if (currentFrame >= totalFrames - 1) {
          animationComplete = true
          this.animations.get(animationId).isComplete = true
          this.finishAnimation(overlay, animationId)
          return
        }
      }

      setTimeout(checkAnimationState, 100)
    }

    setTimeout(checkAnimationState, 500)

    setTimeout(() => {
      if (!animationComplete && !animationError) {
        animationComplete = true
        this.finishAnimation(overlay, animationId)
      }
    }, 5000)
  }

  finishAnimation(overlay, animationId) {
    const animationData = this.animations.get(animationId)
    if (animationData && !animationData.processed) {
      animationData.processed = true
      this.hideSpaLottie(overlay)
      this.revealSlideContent(overlay.closest('.swiper-slide'))
    }
  }

  revealSlideContent(slide) {
    if (slide && hasSpaLottieOverlay(slide)) {
      const slideContent = slide.querySelector('.js-overlay-child')
      if (slideContent) {
        slideContent.style.opacity = '1'
        slideContent.style.visibility = 'visible'
      }
    }
  }

  hideSpaLottie(overlay) {
    overlay.classList.add('is-hidden')
  }

  showSpaLottieLoader() {
    if (!document.querySelector('.spa-lottie-loader')) {
      const loader = document.createElement('div')
      loader.className = 'spa-lottie-loader'
      loader.innerHTML = '<div class="spa-lottie-loader__spinner"></div>'
      document.body.appendChild(loader)
    }
  }

  hideSpaLottieLoader() {
    const loader = document.querySelector('.spa-lottie-loader')
    if (loader) loader.remove()
  }
}

export default SpaLottie
